import { addDays, isoDay } from './taskDateRange'
import { toQueryWindow, windowLengthDays, type QueryWindow, type ReportWindow } from './reportRange'
import {
  DASHBOARD_MODULE_GROUPS,
  DASHBOARD_WIDGETS,
  dashboardModule,
  dashboardWidget,
  isWidgetSize,
  resolveWidgetView,
  widgetViews,
  type DashboardWidgetDef,
  type WidgetSize,
  type WidgetView,
} from './dashboardWidgets'

// ─── Saved dashboards (z8nrz7eqyf) ───────────────────────────────────────────
//
// Two things are stored, both per user and per tenant in `UserPreference`, the
// same way Home layouts are (see homeLayouts.ts for why the tenant rides in the
// key):
//
//   Home dashboard   — the widgets on Home. `null` is the default set, which
//                      is not stored and cannot be edited in place.
//   Named dashboards — the Dashboards page: any number of named dashboards and
//                      the one last viewed.
//
// They are separate keys on purpose: editing a named dashboard never moves a
// widget on Home, and neither touches the Home tile layouts.
//
// A stored value is untrusted (an older shape, a hand-edited row, a value from
// a later version), so everything read goes through a coerce function that
// always returns something renderable.

/** `view` is left out while it is the widget's default, so most stored rows stay `{ id, size }`. */
export type WidgetPlacement = { id: string; size: WidgetSize; view?: WidgetView }

/** Keeps a stored value far below the backend's 32 KB per-key cap. */
export const MAX_WIDGETS_PER_DASHBOARD = 40
export const MAX_DASHBOARDS = 20
export const MAX_DASHBOARD_NAME_LENGTH = 40
const MAX_DASHBOARD_ID_LENGTH = 64

export function homeDashboardKey(tenantId: string): string {
  return `dashboard.home:${tenantId}`
}

export function dashboardsKey(tenantId: string): string {
  return `dashboards:${tenantId}`
}

// ─── One dashboard's widgets ─────────────────────────────────────────────────

/**
 * Whatever is stored, made into a valid widget list: unknown widgets (one
 * removed from the catalog) and repeats are dropped, a missing or unknown size
 * falls back to the widget's default, and the list is capped.
 */
export function coerceWidgetPlacements(raw: unknown): WidgetPlacement[] {
  if (!Array.isArray(raw)) return []
  const seen = new Set<string>()
  const out: WidgetPlacement[] = []
  for (const entry of raw) {
    if (out.length >= MAX_WIDGETS_PER_DASHBOARD) break
    if (typeof entry !== 'object' || entry === null) continue
    const { id, size } = entry as Record<string, unknown>
    if (typeof id !== 'string' || seen.has(id)) continue
    const def = dashboardWidget(id)
    if (!def) continue
    seen.add(id)
    const view = resolveWidgetView(id, (entry as Record<string, unknown>).view)
    const placement: WidgetPlacement = { id, size: isWidgetSize(size) ? size : def.defaultSize }
    if (view !== widgetViews(id)[0]) placement.view = view
    out.push(placement)
  }
  return out
}

/** Moves the widget at `from` to `to`. Same array back for a move that is not one. */
export function moveWidget(list: WidgetPlacement[], from: number, to: number): WidgetPlacement[] {
  if (from === to) return list
  if (from < 0 || to < 0 || from >= list.length || to >= list.length) return list
  const next = [...list]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

/**
 * Puts a widget on the dashboard (at the end, at its default size) or takes it
 * off. Unknown widgets are never added, and nothing is added past the cap.
 */
export function setWidgetShown(
  list: WidgetPlacement[],
  id: string,
  shown: boolean,
): WidgetPlacement[] {
  const has = list.some((w) => w.id === id)
  if (!shown) return has ? list.filter((w) => w.id !== id) : list
  if (has || list.length >= MAX_WIDGETS_PER_DASHBOARD) return list
  const def = dashboardWidget(id)
  return def ? [...list, { id, size: def.defaultSize }] : list
}

export function setWidgetSize(
  list: WidgetPlacement[],
  id: string,
  size: WidgetSize,
): WidgetPlacement[] {
  if (!list.some((w) => w.id === id && w.size !== size)) return list
  return list.map((w) => (w.id === id ? { ...w, size } : w))
}

/**
 * The widgets to store after an edit.
 *
 * The editor only shows widgets the user may see now. One the dashboard held
 * that is hidden now (a role change took the permission away) is kept, at the
 * end, so it comes back by itself if the permission does.
 */
export function mergeHiddenWidgets(
  draft: WidgetPlacement[],
  previous: WidgetPlacement[],
  isAllowed: (id: string) => boolean,
): WidgetPlacement[] {
  const kept = coerceWidgetPlacements(draft)
  const inDraft = new Set(kept.map((w) => w.id))
  return coerceWidgetPlacements([
    ...kept,
    ...previous.filter((w) => !isAllowed(w.id) && !inDraft.has(w.id)),
  ])
}

/** Same widgets, same order, same sizes, same views. */
export function sameWidgets(a: WidgetPlacement[], b: WidgetPlacement[]): boolean {
  return (
    a.length === b.length &&
    a.every(
      (w, i) =>
        w.id === b[i].id && w.size === b[i].size && resolveWidgetView(w.id, w.view) === resolveWidgetView(b[i].id, b[i].view),
    )
  )
}

/** Shows one widget another way. A view the widget does not offer changes nothing. */
export function setWidgetView(list: WidgetPlacement[], id: string, view: WidgetView): WidgetPlacement[] {
  if (!widgetViews(id).includes(view)) return list
  return coerceWidgetPlacements(list.map((w) => (w.id === id ? { ...w, view } : w)))
}

// ─── The Home dashboard ──────────────────────────────────────────────────────

/**
 * What Home shows until the user customises it (z8nrz7eqyv), in order: a row
 * of headline numbers, then the pipeline, then one key widget per module.
 *
 * Widgets not built yet and widgets the role cannot see are skipped, so the
 * default fills in by itself as D3b–D5b land: the default is never stored, so
 * a user on it picks up each new widget without doing anything.
 */
export const DEFAULT_HOME_WIDGETS: WidgetPlacement[] = [
  { id: 'transplant.in-progress', size: 'small' },
  { id: 'upcoming-procedure.next-days', size: 'small' },
  { id: 'collection.awaiting-review', size: 'small' },
  { id: 'patients.active', size: 'small' },
  { id: 'transplant.pipeline', size: 'full' },
  { id: 'transplant.throughput', size: 'wide' },
  { id: 'transplant.at-risk', size: 'wide' },
  { id: 'transplant.by-type', size: 'wide' },
  { id: 'processing.steps-in-progress', size: 'small' },
  { id: 'inventory.below-minimum', size: 'small' },
  { id: 'storage.capacity', size: 'small' },
  { id: 'equipment.maintenance-due', size: 'small' },
  { id: 'capa.open-vs-closed', size: 'small' },
  { id: 'notifications.alerts', size: 'wide' },
]

export function defaultHomeWidgets(
  isAllowed: (id: string) => boolean,
  isBuilt: (id: string) => boolean,
): WidgetPlacement[] {
  return DEFAULT_HOME_WIDGETS.filter((w) => isAllowed(w.id) && isBuilt(w.id))
}

/**
 * `null` is the default set: nothing stored, or nothing usable stored.
 *
 * Reads `{ widgets: [...] }`, the shape `homeDashboardValue` writes, and also a
 * bare list: that is what was written before the shapes were made to match,
 * and a user's saved Home must not be thrown away for it.
 */
export function coerceHomeDashboard(raw: unknown): WidgetPlacement[] | null {
  if (Array.isArray(raw)) return coerceWidgetPlacements(raw)
  if (typeof raw !== 'object' || raw === null) return null
  const widgets = (raw as Record<string, unknown>).widgets
  return Array.isArray(widgets) ? coerceWidgetPlacements(widgets) : null
}

/** What is stored for a saved Home: always the shape `coerceHomeDashboard` reads. */
export function homeDashboardValue(widgets: WidgetPlacement[]): { widgets: WidgetPlacement[] } {
  return { widgets: coerceWidgetPlacements(widgets) }
}

// ─── Named dashboards ────────────────────────────────────────────────────────

export interface NamedDashboard {
  id: string
  name: string
  widgets: WidgetPlacement[]
}

export interface DashboardsPrefs {
  /** The dashboard the Dashboards page opens on; null when there are none. */
  defaultId: string | null
  dashboards: NamedDashboard[]
}

/**
 * The three dashboards every user starts with (z8nrz7eqyu). They are not
 * stored until the user changes something, so a user who never edits keeps
 * getting the latest version of them, and widgets listed here that are not
 * built yet appear by themselves once they are (they are hidden until then).
 *
 * Once the user edits anything, the whole set becomes their own copy, fully
 * editable, renamable and deletable. A starter's id says where it came from,
 * which is what "Restore original" reads. Ids are permanent.
 */
export const STARTER_DASHBOARDS: NamedDashboard[] = [
  {
    id: 'starter:overview',
    name: 'Overview',
    widgets: [
      { id: 'transplant.in-progress', size: 'small' },
      { id: 'upcoming-procedure.next-days', size: 'small' },
      { id: 'collection.awaiting-review', size: 'small' },
      { id: 'patients.active', size: 'small' },
      { id: 'inventory.below-minimum', size: 'small' },
      { id: 'storage.capacity', size: 'small' },
      { id: 'equipment.by-status', size: 'small' },
      { id: 'capa.overdue', size: 'small' },
      { id: 'transplant.pipeline', size: 'full' },
      { id: 'transplant.throughput', size: 'wide' },
      { id: 'transplant.at-risk', size: 'wide' },
      { id: 'equipment.maintenance-due', size: 'small' },
      { id: 'pending-inventory.count', size: 'small' },
      { id: 'notifications.alerts', size: 'wide' },
    ],
  },
  {
    id: 'starter:patients-quality',
    name: 'Transplant & Patients',
    widgets: [
      { id: 'patients.new', size: 'small' },
      { id: 'patients.active', size: 'small' },
      { id: 'transplant.completed', size: 'small' },
      { id: 'collection.awaiting-review', size: 'small' },
      { id: 'transplant.pipeline', size: 'full' },
      { id: 'transplant.throughput', size: 'wide' },
      { id: 'transplant.by-type', size: 'wide', view: 'donut' },
      { id: 'transplant.at-risk', size: 'wide' },
      { id: 'upcoming-procedure.next-days', size: 'wide', view: 'columns' },
      { id: 'transplant.in-progress', size: 'wide', view: 'bars' },
    ],
  },
  {
    id: 'starter:process',
    name: 'Manufacturing & Process',
    widgets: [
      { id: 'processing.steps-in-progress', size: 'small' },
      { id: 'clean.due', size: 'small' },
      { id: 'pick-lists.open', size: 'small' },
      { id: 'inventory.below-minimum', size: 'small' },
      { id: 'mes.runs-by-status', size: 'wide' },
      { id: 'kit.builds-by-status', size: 'wide' },
      { id: 'pick-lists.shortages', size: 'wide' },
      { id: 'processing.overdue-steps', size: 'wide' },
    ],
  },
  {
    id: 'starter:inventory',
    name: 'Inventory',
    widgets: [
      { id: 'inventory.below-minimum', size: 'small' },
      { id: 'inventory.lots-on-hand', size: 'small' },
      { id: 'pending-inventory.count', size: 'small' },
      { id: 'storage.capacity', size: 'small' },
      { id: 'inventory.stock-by-category', size: 'wide' },
      { id: 'inventory.expiring', size: 'wide' },
      { id: 'inventory.consumption', size: 'wide' },
      { id: 'inventory.top-consumed', size: 'wide' },
      { id: 'pick-lists.shortages', size: 'full' },
    ],
  },
  {
    id: 'starter:storage',
    name: 'Storage',
    widgets: [
      { id: 'storage.capacity', size: 'small' },
      { id: 'inventory.lots-on-hand', size: 'small' },
      { id: 'pending-inventory.count', size: 'small' },
      { id: 'inventory.below-minimum', size: 'small' },
      { id: 'storage.capacity-by-type', size: 'wide' },
      { id: 'storage.units', size: 'wide' },
      { id: 'inventory.expiring', size: 'full', view: 'donut' },
    ],
  },
  {
    id: 'starter:products',
    name: 'Products & Shipping',
    widgets: [
      { id: 'products.released', size: 'small' },
      { id: 'storage.capacity', size: 'small' },
      { id: 'products.by-status', size: 'wide' },
      { id: 'shipping.by-status', size: 'wide' },
      { id: 'shipping.by-partner', size: 'wide' },
      { id: 'products.by-catalog', size: 'wide' },
    ],
  },
  {
    id: 'starter:equipment',
    name: 'Equipment & Resources',
    widgets: [
      { id: 'equipment.by-status', size: 'small' },
      { id: 'equipment.maintenance-due', size: 'small' },
      { id: 'equipment.reservations-today', size: 'small' },
      { id: 'storage.capacity', size: 'small' },
      { id: 'equipment.maintenance-list', size: 'wide' },
      { id: 'resource.availability', size: 'wide' },
      { id: 'equipment.maintenance-done', size: 'wide' },
      { id: 'equipment.by-type', size: 'wide' },
    ],
  },
  {
    id: 'starter:quality',
    name: 'Quality & CAPA',
    widgets: [
      { id: 'capa.open-vs-closed', size: 'full' },
      { id: 'capa.overdue', size: 'small' },
      { id: 'capa.time-to-close', size: 'small' },
      { id: 'collection.awaiting-review', size: 'small' },
      { id: 'integration.failure-list', size: 'small', view: 'number' },
      { id: 'capa.trend', size: 'wide' },
      { id: 'capa.ageing', size: 'wide' },
      { id: 'audit.recent', size: 'wide' },
      { id: 'transplant.at-risk', size: 'wide' },
    ],
  },
  {
    id: 'starter:system',
    name: 'System Activity',
    widgets: [
      { id: 'notifications.alerts', size: 'wide' },
      { id: 'notifications.by-category', size: 'wide' },
      { id: 'integration.failures', size: 'wide' },
      { id: 'integration.failure-list', size: 'wide' },
      { id: 'audit.activity', size: 'full' },
      { id: 'audit.recent', size: 'wide' },
    ],
  },
]

/** The starter a dashboard was made from, if it was. */
export function starterOf(id: string): NamedDashboard | undefined {
  return STARTER_DASHBOARDS.find((d) => d.id === id)
}

/** What a user has before they change anything: the starters, Overview opening first. */
export function starterDashboardsPrefs(): DashboardsPrefs {
  return {
    defaultId: STARTER_DASHBOARDS[0].id,
    dashboards: STARTER_DASHBOARDS.map((d) => ({ ...d, widgets: [...d.widgets] })),
  }
}

/** Kept for callers that want "nothing at all"; a fresh user gets the starters instead. */
export const DEFAULT_DASHBOARDS_PREFS: DashboardsPrefs = { defaultId: null, dashboards: [] }

/**
 * Whatever is stored, made into valid prefs.
 *
 * Nothing stored means the starters. Something stored is the user's own set,
 * even an empty one: a user who deleted every dashboard does not get the
 * starters back on their next visit. Duplicate ids collapse to the first,
 * nameless entries are dropped, and a default with no dashboard falls back to
 * the first one there is. `active` is read as the default too, for any value
 * stored before the field was renamed.
 */
export function coerceDashboardsPrefs(raw: unknown): DashboardsPrefs {
  if (raw === undefined || raw === null) return starterDashboardsPrefs()
  const obj = typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const dashboards: NamedDashboard[] = []
  const ids = new Set<string>()
  for (const entry of Array.isArray(obj.dashboards) ? obj.dashboards : []) {
    if (dashboards.length >= MAX_DASHBOARDS) break
    if (typeof entry !== 'object' || entry === null) continue
    const { id, name, widgets } = entry as Record<string, unknown>
    if (typeof id !== 'string' || id.length === 0 || id.length > MAX_DASHBOARD_ID_LENGTH) continue
    if (ids.has(id) || typeof name !== 'string') continue
    const trimmed = name.trim().slice(0, MAX_DASHBOARD_NAME_LENGTH)
    if (!trimmed) continue
    ids.add(id)
    dashboards.push({ id, name: trimmed, widgets: coerceWidgetPlacements(widgets) })
  }
  const wanted = typeof obj.defaultId === 'string' ? obj.defaultId : obj.active
  const defaultId = typeof wanted === 'string' && ids.has(wanted) ? wanted : (dashboards[0]?.id ?? null)
  return { defaultId, dashboards }
}

/**
 * Never: once a user has changed anything, the set is theirs and is stored,
 * including an empty one (deleting the key would bring the starters back).
 */
export function isDefaultDashboardsPrefs(_prefs: DashboardsPrefs): boolean {
  return false
}

export function defaultDashboard(prefs: DashboardsPrefs): NamedDashboard | null {
  return prefs.dashboards.find((d) => d.id === prefs.defaultId) ?? prefs.dashboards[0] ?? null
}

/** A user-facing problem with a dashboard name, or null when it can be saved. */
export function validateDashboardName(
  prefs: DashboardsPrefs,
  name: string,
  exceptId?: string,
): string | null {
  const trimmed = name.trim()
  if (!trimmed) return 'Enter a name.'
  if (trimmed.length > MAX_DASHBOARD_NAME_LENGTH) {
    return `Use ${MAX_DASHBOARD_NAME_LENGTH} characters or fewer.`
  }
  const lower = trimmed.toLowerCase()
  if (prefs.dashboards.some((d) => d.id !== exceptId && d.name.toLowerCase() === lower)) {
    return 'You already have a dashboard with this name.'
  }
  return null
}

/**
 * A name for a new dashboard made from `base` that the user does not already
 * have: the base itself, else "base 2", "base 3"… ("Overview" is taken by the
 * starter, so a copy of it becomes "Overview 2" rather than an error).
 */
export function uniqueDashboardName(prefs: DashboardsPrefs, base: string): string {
  const taken = new Set(prefs.dashboards.map((d) => d.name.toLowerCase()))
  const stem = base.trim().slice(0, MAX_DASHBOARD_NAME_LENGTH - 3)
  if (!taken.has(stem.toLowerCase())) return stem
  for (let n = 2; n < 100; n++) {
    const candidate = `${stem} ${n}`
    if (!taken.has(candidate.toLowerCase())) return candidate
  }
  return stem
}

export function canAddDashboard(prefs: DashboardsPrefs): boolean {
  return prefs.dashboards.length < MAX_DASHBOARDS
}

/** Adds a dashboard at the end. Past the cap, nothing changes. */
export function addDashboard(prefs: DashboardsPrefs, dashboard: NamedDashboard): DashboardsPrefs {
  if (!canAddDashboard(prefs)) return prefs
  return coerceDashboardsPrefs({ ...prefs, dashboards: [...prefs.dashboards, dashboard] })
}

export function renameDashboard(prefs: DashboardsPrefs, id: string, name: string): DashboardsPrefs {
  if (!prefs.dashboards.some((d) => d.id === id)) return prefs
  return coerceDashboardsPrefs({
    ...prefs,
    dashboards: prefs.dashboards.map((d) => (d.id === id ? { ...d, name } : d)),
  })
}

/** Replaces a dashboard's widgets. */
export function saveDashboardWidgets(
  prefs: DashboardsPrefs,
  id: string,
  widgets: WidgetPlacement[],
): DashboardsPrefs {
  if (!prefs.dashboards.some((d) => d.id === id)) return prefs
  return coerceDashboardsPrefs({
    ...prefs,
    dashboards: prefs.dashboards.map((d) => (d.id === id ? { ...d, widgets } : d)),
  })
}

/** Removes a dashboard. Deleting the default makes the first one left the default. */
export function deleteDashboard(prefs: DashboardsPrefs, id: string): DashboardsPrefs {
  return coerceDashboardsPrefs({
    defaultId: prefs.defaultId === id ? null : prefs.defaultId,
    dashboards: prefs.dashboards.filter((d) => d.id !== id),
  })
}

/** Makes a dashboard the one the Dashboards page opens on. */
export function setDefaultDashboard(prefs: DashboardsPrefs, id: string): DashboardsPrefs {
  return prefs.dashboards.some((d) => d.id === id) ? { ...prefs, defaultId: id } : prefs
}

/** Puts a starter's original widgets back. Its name is the user's, so it stays. */
export function restoreStarter(prefs: DashboardsPrefs, id: string): DashboardsPrefs {
  const starter = starterOf(id)
  return starter ? saveDashboardWidgets(prefs, id, [...starter.widgets]) : prefs
}

// ─── Presets for a new named dashboard (z8nrz7eqyu) ──────────────────────────

export type DashboardPresetId = 'empty' | 'all' | string

/**
 * Empty, a copy of one of the starter dashboards, one per module group, or
 * everything. Group ids are the catalog's; starter ids are the starters'.
 */
export const DASHBOARD_PRESETS: { id: DashboardPresetId; label: string }[] = [
  { id: 'empty', label: 'Empty' },
  ...STARTER_DASHBOARDS.map((d) => ({ id: d.id, label: d.name })),
  ...DASHBOARD_MODULE_GROUPS.map((g) => ({ id: g.id, label: g.title })),
  { id: 'all', label: 'Everything' },
]

/**
 * The widgets a new dashboard starts with: every built widget the user may see
 * in the chosen group (or in all of them), at its default size, in catalog
 * order. Capped like any other dashboard.
 */
export function presetWidgets(
  preset: DashboardPresetId,
  isAllowed: (id: string) => boolean,
  isBuilt: (id: string) => boolean,
): WidgetPlacement[] {
  if (preset === 'empty') return []
  // A starter copied whole, unbuilt widgets included: the page hides those
  // until they are built, then they appear without the user doing anything.
  const starter = starterOf(preset)
  if (starter) return starter.widgets.filter((w) => isAllowed(w.id)).map((w) => ({ ...w }))
  const picked = DASHBOARD_WIDGETS.filter((w) => {
    if (!isAllowed(w.id) || !isBuilt(w.id)) return false
    return preset === 'all' || dashboardModule(w.module)?.group === preset
  })
  return coerceWidgetPlacements(picked.map((w) => ({ id: w.id, size: w.defaultSize })))
}

// ─── The Add widgets picker ──────────────────────────────────────────────────

export const ADD_WIDGETS_FILTERS = [
  { id: 'all', title: 'All' },
  ...DASHBOARD_MODULE_GROUPS.map((g) => ({ id: g.id, title: g.title })),
]

export type AddWidgetsRow = {
  widget: DashboardWidgetDef
  moduleLabel: string
  shown: boolean
  /** Whether a component exists for it yet. Unbuilt widgets are listed, not offered. */
  built: boolean
}

/**
 * The rows of the Add widgets list, in catalog order (so a module's widgets
 * sit together), filtered by group and by a search over title, description
 * and module name. Only widgets the user may see are ever listed.
 */
export function addWidgetsRows(
  draft: WidgetPlacement[],
  allowed: DashboardWidgetDef[],
  isBuilt: (id: string) => boolean,
  query: string,
  groupFilter: string,
): AddWidgetsRow[] {
  const onIds = new Set(draft.map((w) => w.id))
  const allowedIds = new Set(allowed.map((w) => w.id))
  const q = query.trim().toLowerCase()
  return DASHBOARD_WIDGETS.filter((w) => allowedIds.has(w.id)).flatMap((widget) => {
    const module = dashboardModule(widget.module)
    if (!module) return []
    if (groupFilter !== 'all' && module.group !== groupFilter) return []
    const haystack = `${widget.title} ${widget.description} ${module.label}`.toLowerCase()
    if (q && !haystack.includes(q)) return []
    return [{ widget, moduleLabel: module.label, shown: onIds.has(widget.id), built: isBuilt(widget.id) }]
  })
}

// ─── The period every widget reads ───────────────────────────────────────────
//
// Rolling windows ending today, built on the Reports helpers so "last 7 days"
// means the same days on a dashboard as in a report. `toQueryWindow` is the
// one place the inclusive window becomes the half-open range endpoints take.

/** A preset, or `custom`: a from–to the user picked (see `customTimeWindow`). */
export type DashboardWindowId = 'today' | '7d' | '30d' | '90d' | 'custom'

export const DASHBOARD_WINDOWS: { id: DashboardWindowId; label: string; days: number }[] = [
  { id: 'today', label: 'Today', days: 1 },
  { id: '7d', label: 'Last 7 days', days: 7 },
  { id: '30d', label: 'Last 30 days', days: 30 },
  { id: '90d', label: 'Last 90 days', days: 90 },
]

export const DEFAULT_DASHBOARD_WINDOW: DashboardWindowId = '30d'

export function isDashboardWindowId(value: unknown): value is DashboardWindowId {
  return DASHBOARD_WINDOWS.some((w) => w.id === value)
}

export type DashboardTimeWindow = {
  id: DashboardWindowId
  /** Inclusive, for display. */
  window: ReportWindow
  /** Half-open, for endpoints. */
  query: QueryWindow
}

export function dashboardTimeWindow(id: DashboardWindowId, today: Date): DashboardTimeWindow {
  const days = DASHBOARD_WINDOWS.find((w) => w.id === id)?.days ?? 30
  const to = isoDay(today)
  const window = { from: addDays(to, -(days - 1)), to }
  return { id, window, query: toQueryWindow(window) }
}

/** The longest custom range: a year, inside the endpoints' 400-day cap. */
export const MAX_CUSTOM_RANGE_DAYS = 366

/** A user-facing problem with a custom range, or null when it can be used. */
export function validateCustomRange(from: string, to: string, today: Date): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) return 'Pick a start and an end date.'
  if (to < from) return 'The end date is before the start date.'
  if (to > isoDay(today)) return 'The range cannot end after today.'
  if (windowLengthDays({ from, to }) > MAX_CUSTOM_RANGE_DAYS) return `Pick ${MAX_CUSTOM_RANGE_DAYS} days or fewer.`
  return null
}

/** A from–to the user picked, both days inclusive, as every widget reads a window. */
export function customTimeWindow(from: string, to: string): DashboardTimeWindow {
  const window = { from, to }
  return { id: 'custom', window, query: toQueryWindow(window) }
}

/**
 * The same number of days immediately before a window: what a widget compares
 * itself with ("▲ 12% vs the previous 30 days"). Works for a custom range too:
 * its own length, ending the day before it starts.
 */
export function previousTimeWindow(time: DashboardTimeWindow): { window: ReportWindow; query: QueryWindow } {
  const days = windowLengthDays(time.window)
  const to = addDays(time.window.from, -1)
  const window = { from: addDays(to, -(days - 1)), to }
  return { window, query: toQueryWindow(window) }
}
